import React, { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ComparisonDimension, DimensionType } from '../../../services/benchmarkService';
import { count } from '../BenchmarkVocabulary';
import { DIMENSION_TYPE_PLURALS, coverageBand, coverageGapLine, producerLabel } from './investigationVocabulary';
import { dsVar, ink } from '../../../theme/tokens';

/** The order the tabs present the kinds in, which every coverage group follows. */
export const COVERAGE_TYPE_ORDER: DimensionType[] = ['decision', 'agent', 'factor', 'typology'];

/** How many items needing a look a group lists before "Show more". */
const SHOWN = 5;

interface CoverageSummaryProps {
    dims: ComparisonDimension[];
    /** Opens the item on the tab it belongs to. Without it the items are plain text. */
    onOpenItem?: (dimension: ComparisonDimension) => void;
}

const headSx = {
    textAlign: 'left' as const, px: 1.5, py: 1, fontSize: '0.6875rem', fontWeight: 600, color: dsVar('fg-muted'),
    textTransform: 'uppercase' as const, letterSpacing: '0.05em'
};
const linkSx = {
    p: 0, minWidth: 0, textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, color: dsVar('accent-fg'),
    justifyContent: 'flex-start', textAlign: 'left', '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
};

/** Worst first: compared on no case, then the least-covered. */
const worstFirst = (a: ComparisonDimension, b: ComparisonDimension) =>
    a.comparable / (a.subjects || 1) - b.comparable / (b.subjects || 1);

/** The group's result in one sentence: "12 of 14 compared on every case; 1 on some, 1 on none." */
function groupSentence(items: ComparisonDimension[]): string {
    const full = items.filter((d) => coverageBand(d) === 'full').length;
    const partial = items.filter((d) => coverageBand(d) === 'partial').length;
    const none = items.length - full - partial;
    if (full === items.length) return items.length === 1 ? 'Compared on every case.' : `All ${count(items.length)} compared on every case.`;
    const rest = [partial ? `${count(partial)} on some cases` : null, none ? `${count(none)} on none` : null].filter(Boolean).join(', ');
    return `${count(full)} of ${count(items.length)} compared on every case; ${rest}.`;
}

const ItemName: React.FC<{ dimension: ComparisonDimension; onOpenItem?: (d: ComparisonDimension) => void }> = ({ dimension, onOpenItem }) => (
    <Box>
        {onOpenItem
            ? <Button disableRipple onClick={() => onOpenItem(dimension)} sx={linkSx}>{dimension.label}</Button>
            : <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg-body') }}>{dimension.label}</Typography>}
        {dimension.dimensionType === 'factor' && dimension.producer && (
            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                {dimension.producerLabel ?? producerLabel(dimension.producer)}
            </Typography>
        )}
    </Box>
);

/** One kind's coverage: a sentence, then only the items that were not compared on every case. */
const CoverageGroup: React.FC<{ type: DimensionType; items: ComparisonDimension[]; onOpenItem?: (d: ComparisonDimension) => void }> = ({ type, items, onOpenItem }) => {
    const [expanded, setExpanded] = useState(false);
    const problems = useMemo(() => items.filter((d) => coverageBand(d) !== 'full').sort(worstFirst), [items]);
    const shown = expanded ? problems : problems.slice(0, SHOWN);
    const clean = problems.length === 0;
    const tone = clean ? '#10B981' : '#FBBF24';

    return (
        <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '8px', p: 2 }} data-testid={`coverage-${type}`}>
            <Box className="flex flex-wrap items-center justify-between gap-2">
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: dsVar('fg-body') }}>{DIMENSION_TYPE_PLURALS[type]}</Typography>
                <Box component="span" sx={{
                    px: 1, py: 0.25, borderRadius: '999px', fontSize: '0.6875rem', fontWeight: 700, color: ink(tone),
                    backgroundColor: `${tone}1A`, border: `1px solid ${tone}4D`
                }}>
                    {clean ? 'All compared' : `${count(problems.length)} to check`}
                </Box>
            </Box>
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), mt: 0.5 }}>{groupSentence(items)}</Typography>

            {!clean && (
                <Box sx={{ mt: 1.5 }}>
                    {shown.map((d) => (
                        <Box
                            key={d.dimensionKey}
                            sx={{
                                display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(12rem, 1.2fr) 11rem minmax(12rem, 2fr)' },
                                gap: { xs: 0.25, md: 2 }, py: 1, borderTop: `1px solid ${dsVar('line')}`, alignItems: 'baseline'
                            }}
                        >
                            <ItemName dimension={d} onOpenItem={onOpenItem} />
                            <Typography sx={{ fontSize: '0.8125rem', color: d.comparable ? dsVar('fg-soft') : ink('#FBBF24'), fontVariantNumeric: 'tabular-nums' }}>
                                {d.comparable ? `Compared on ${count(d.comparable)} of ${count(d.subjects)}` : 'Compared on no case'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>{coverageGapLine(d) || '—'}</Typography>
                        </Box>
                    ))}
                    {problems.length > SHOWN && (
                        <Button onClick={() => setExpanded((v) => !v)} sx={{ mt: 0.5, p: 0, textTransform: 'none', fontSize: '0.75rem', color: dsVar('fg-2'), '&:hover': { color: dsVar('accent-fg'), backgroundColor: 'transparent' } }}>
                            {expanded ? 'Show fewer' : `Show ${count(problems.length - SHOWN)} more`}
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
};

/** Every item, grouped by kind: the audit view, collapsed by default. */
const AllItemsTable: React.FC<{ groups: [DimensionType, ComparisonDimension[]][] }> = ({ groups }) => (
    <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '8px', overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', minWidth: 640, borderCollapse: 'collapse' }}>
            <Box component="thead">
                <Box component="tr" sx={{ borderBottom: `1px solid ${dsVar('line')}` }}>
                    {['Item', 'Cases compared', 'Why the rest were not compared'].map((c) => <Box key={c} component="th" sx={headSx}>{c}</Box>)}
                </Box>
            </Box>
            <Box component="tbody">
                {groups.map(([type, items]) => (
                    <React.Fragment key={type}>
                        <Box component="tr" sx={{ borderTop: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('page', 0.35) }}>
                            <Box component="td" colSpan={3} sx={{ ...headSx, color: dsVar('fg-2') }}>{DIMENSION_TYPE_PLURALS[type]}</Box>
                        </Box>
                        {items.map((d) => (
                            <Box key={d.dimensionKey} component="tr" sx={{ borderTop: `1px solid ${dsVar('line')}` }}>
                                <Box component="td" sx={{ px: 1.5, py: 1, fontSize: '0.8125rem', color: dsVar('fg-body') }}>{d.label}</Box>
                                <Box component="td" sx={{ px: 1.5, py: 1, fontSize: '0.8125rem', color: d.comparable ? dsVar('fg-soft') : ink('#FBBF24'), fontVariantNumeric: 'tabular-nums' }}>
                                    {count(d.comparable)} / {count(d.subjects)}
                                </Box>
                                <Box component="td" sx={{ px: 1.5, py: 1, fontSize: '0.75rem', color: dsVar('fg-2') }}>{coverageGapLine(d) || '—'}</Box>
                            </Box>
                        ))}
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    </Box>
);

/**
 * Whether the run's evidence is good enough to judge it by: per kind (decisions,
 * agent verdicts, red flags, typologies), how many items could be compared on every
 * case, and only the ones that could not, with why. Each item's own counts are on
 * its tab's scorecard; this is the run-wide view, so it leads with the exceptions.
 */
const CoverageSummary: React.FC<CoverageSummaryProps> = ({ dims, onOpenItem }) => {
    const [showAll, setShowAll] = useState(false);
    const groups = useMemo(() => COVERAGE_TYPE_ORDER
        .map((type) => [type, dims.filter((d) => d.dimensionType === type)] as [DimensionType, ComparisonDimension[]])
        .filter(([, items]) => items.length > 0), [dims]);

    if (!groups.length) {
        return <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>This run compared nothing.</Typography>;
    }
    return (
        <Box className="flex flex-col gap-3">
            {groups.map(([type, items]) => <CoverageGroup key={type} type={type} items={items} onOpenItem={onOpenItem} />)}
            <Box>
                <Button onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}
                    sx={{ p: 0, textTransform: 'none', fontSize: '0.8125rem', color: dsVar('fg-2'), '&:hover': { color: dsVar('accent-fg'), backgroundColor: 'transparent' } }}>
                    {showAll ? 'Hide the full list' : `Show every item (${count(dims.length)})`}
                </Button>
                {showAll && <Box sx={{ mt: 1.5 }}><AllItemsTable groups={groups} /></Box>}
            </Box>
        </Box>
    );
};

export default CoverageSummary;
